import { useState, useCallback, useRef } from 'react'
import { format } from 'date-fns'
import { pdf } from '@react-pdf/renderer'
import { createElement } from 'react'
import { makeApiCall } from '../../utils/customerUtils'
import { ReportPDFDocument } from './ReportPDFDocument'
import type { Report, ReportData, DateRange } from './types'

interface UseReportDataReturn {
  reportData: ReportData | null
  loading: boolean
  error: string | null
  loadReport: (report: Report, dateRange: DateRange, filters: Record<string, string>) => Promise<void>
  exportToCSV: (report: Report) => void
  exportToPDF: (report: Report, dateRange: DateRange) => Promise<void>
  clearData: () => void
}

export function useReportData(): UseReportDataReturn {
  const [reportData, setReportData] = useState<ReportData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const loadingRef = useRef(false)

  const loadReport = useCallback(async (
    report: Report,
    dateRange: DateRange,
    filters: Record<string, string>
  ) => {
    if (loadingRef.current) return
    loadingRef.current = true
    setLoading(true)
    setError(null)
    setReportData(null)

    try {
      if (!report.endpoint) throw new Error('Report endpoint not configured')

      const params = new URLSearchParams()
      if (report.requiresDateRange) {
        params.append('start_date', dateRange.start)
        params.append('end_date', dateRange.end)
      }
      if (report.requiresFilters && report.availableFilters) {
        report.availableFilters.forEach(key => {
          if (filters[key]) params.append(key, filters[key])
        })
      }

      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 30000)
      const response = await makeApiCall(`${report.endpoint}?${params}`, { signal: controller.signal })
      clearTimeout(timeoutId)

      if (!response.ok) {
        const text = await response.text()
        throw new Error(`${response.status} ${response.statusText} — ${text}`)
      }
      if (!response.headers.get('content-type')?.includes('application/json')) {
        throw new Error('Server did not return JSON data')
      }

      setReportData(await response.json())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load report')
    } finally {
      setLoading(false)
      loadingRef.current = false
    }
  }, [])

  const exportToCSV = useCallback((report: Report) => {
    if (!reportData) return
    const csvContent = [
      reportData.headers.join(','),
      ...reportData.rows.map(row =>
        row.map(cell => {
          const s = cell?.toString() || ''
          return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s
        }).join(',')
      )
    ].join('\n')

    const link = document.createElement('a')
    link.href = URL.createObjectURL(new Blob([csvContent], { type: 'text/csv;charset=utf-8;' }))
    link.download = `${report.id}_${format(new Date(), 'yyyy-MM-dd')}.csv`
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [reportData])

  const exportToPDF = useCallback(async (report: Report, dateRange: DateRange) => {
    if (!reportData) return
    try {
      const doc = createElement(ReportPDFDocument, {
        data: reportData,
        title: report.name,
        dateRange: report.requiresDateRange ? dateRange : undefined
      })
      const blob = await pdf(doc).toBlob()
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `${report.id}_${format(new Date(), 'yyyy-MM-dd')}.pdf`
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch {
      setError('Failed to generate PDF')
    }
  }, [reportData])

  const clearData = useCallback(() => {
    setReportData(null)
    setError(null)
  }, [])

  return { reportData, loading, error, loadReport, exportToCSV, exportToPDF, clearData }
}
